import { isPlatformBrowser } from '@angular/common';
import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import Swal from 'sweetalert2';
import { AtwReviewService } from '../../../../services/atw-review';

@Component({
  selector: 'app-addreview',
  templateUrl: './addreview.component.html',
  styleUrl: './addreview.component.scss',
})
export class AddreviewComponent implements OnInit {
  reviewForm!: FormGroup;

  isSubmitting = false;
  isCheckingReview = true;
  isAr = false;

  myReview: any = null;

  stars = [1, 2, 3, 4, 5];

  private isBrowser: boolean;

  constructor(
    private fb: FormBuilder,
    private reviewService: AtwReviewService,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    this.isAr = this.getLocalStorageItem('language') === 'ar';

    this.createForm();

    if (!this.isLoggedIn()) {
      this.isCheckingReview = false;

      Swal.fire({
        icon: 'warning',
        title: this.isAr ? 'لازم تسجل دخول الأول' : 'Login required',
        text: this.isAr
          ? 'من فضلك سجل دخولك عشان تقدر تضيف تقييم.'
          : 'Please login first to add your review.',
        confirmButtonText: this.isAr ? 'تسجيل الدخول' : 'Login',
      }).then(() => {
        this.router.navigate(['/login']);
      });

      return;
    }

    this.patchUserData();
    this.loadMyReview();
  }

  private createForm(): void {
    this.reviewForm = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(255)]],
      job_title: ['', [Validators.maxLength(255)]],
      company_name: ['', [Validators.maxLength(255)]],
      stars: [5, [Validators.required, Validators.min(1), Validators.max(5)]],
      text: ['', [Validators.required, Validators.minLength(10)]],
    });
  }

  private loadMyReview(): void {
    const userId = this.getLocalStorageItem('user_id');

    if (!userId) {
      this.isCheckingReview = false;
      return;
    }

    this.isCheckingReview = true;

    this.reviewService
      .getReviews({
        lang: this.isAr ? 'ar' : 'en',
        include_inactive: 1,
        per_page: 1000,
      })
      .subscribe({
        next: (res) => {
          const reviews = res?.reviews || [];

          this.myReview =
            reviews.find((review: any) => String(review.user_id) === String(userId)) ||
            null;

          this.isCheckingReview = false;
        },
        error: () => {
          this.isCheckingReview = false;

          Swal.fire({
            icon: 'error',
            title: this.isAr ? 'حدث خطأ' : 'Something went wrong',
            text: this.isAr
              ? 'تعذر التأكد من التقييمات السابقة.'
              : 'Unable to check your previous review.',
            confirmButtonText: this.isAr ? 'تمام' : 'OK',
          });
        },
      });
  }

  submitReview(): void {
    if (!this.isLoggedIn()) {
      Swal.fire({
        icon: 'warning',
        title: this.isAr ? 'لازم تسجل دخول الأول' : 'Login required',
        text: this.isAr
          ? 'من فضلك سجل دخولك عشان تقدر تضيف تقييم.'
          : 'Please login first to add your review.',
        confirmButtonText: this.isAr ? 'تمام' : 'OK',
      }).then(() => {
        this.router.navigate(['/login']);
      });

      return;
    }

    if (this.myReview) {
      Swal.fire({
        icon: 'info',
        title: this.isAr ? 'لديك تقييم بالفعل' : 'You already submitted a review',
        text: this.isAr
          ? 'لا يمكنك إرسال أكثر من تقييم بنفس الحساب.'
          : 'You cannot submit more than one review with the same account.',
        confirmButtonText: this.isAr ? 'تمام' : 'OK',
      });

      return;
    }

    if (this.reviewForm.invalid || this.isSubmitting) {
      this.reviewForm.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;

    const formValue = this.reviewForm.value;

    const userId = this.getLocalStorageItem('user_id');
    const userType = this.getLocalStorageItem('user_type');

    const formData = new FormData();

    if (userId) {
      formData.append('user_id', userId);
    }

    formData.append('user_type', this.normalizeUserType(userType));

    formData.append('name_en', formValue.name);
    formData.append('name_ar', formValue.name);

    formData.append('text_en', formValue.text);
    formData.append('text_ar', formValue.text);

    formData.append('job_title_en', formValue.job_title || '');
    formData.append('job_title_ar', formValue.job_title || '');

    formData.append('company_name_en', formValue.company_name || '');
    formData.append('company_name_ar', formValue.company_name || '');

    formData.append('stars', String(formValue.stars));
    formData.append('status', 'pending');
    formData.append('is_featured', '0');
    formData.append('sort_order', '0');

    this.reviewService.addReview(formData).subscribe({
      next: (res) => {
        this.isSubmitting = false;

        this.myReview =
          res?.review || {
            user_id: userId,
            user_type: this.normalizeUserType(userType),
            name: formValue.name,
            text: formValue.text,
            stars: formValue.stars,
            job_title: formValue.job_title,
            company_name: formValue.company_name,
            status: 'pending',
          };

        Swal.fire({
          icon: 'success',
          title: this.isAr ? 'تم إرسال التقييم' : 'Review Submitted',
          text: this.isAr
            ? 'شكرًا لك، سيتم مراجعة التقييم قبل ظهوره.'
            : 'Thank you, your review will be reviewed before publishing.',
          confirmButtonText: this.isAr ? 'تمام' : 'OK',
        });

        this.resetForm();
      },
      error: (error) => {
        this.isSubmitting = false;

        Swal.fire({
          icon: 'error',
          title: this.isAr ? 'حدث خطأ' : 'Something went wrong',
          text:
            error?.error?.message ||
            (this.isAr
              ? 'تعذر إرسال التقييم، حاول مرة أخرى.'
              : 'Unable to submit your review. Please try again.'),
          confirmButtonText: this.isAr ? 'تمام' : 'OK',
        });
      },
    });
  }

  setStars(value: number): void {
    this.reviewForm.patchValue({
      stars: value,
    });

    this.reviewForm.get('stars')?.markAsTouched();
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.reviewForm.get(fieldName);

    return !!field && field.invalid && (field.dirty || field.touched);
  }

  getStatusKey(status: string | null | undefined): string {
    switch (status) {
      case 'active':
        return 'addReview.status.active';

      case 'inactive':
        return 'addReview.status.inactive';

      case 'pending':
        return 'addReview.status.pending';

      default:
        return 'addReview.status.pending';
    }
  }

  private resetForm(): void {
    this.reviewForm.reset({
      name: this.getUserFullName(),
      job_title: '',
      company_name: '',
      stars: 5,
      text: '',
    });
  }

  private patchUserData(): void {
    const fullName = this.getUserFullName();

    if (fullName) {
      this.reviewForm.patchValue({
        name: fullName,
      });
    }
  }

  private getUserFullName(): string {
    const firstName = this.getLocalStorageItem('first_name') || '';
    const lastName = this.getLocalStorageItem('last_name') || '';
    const username = this.getLocalStorageItem('username') || '';

    const fullName = `${firstName} ${lastName}`.trim();

    return fullName || username;
  }

  private isLoggedIn(): boolean {
    const token = this.getLocalStorageItem('token');
    const userId = this.getLocalStorageItem('user_id');

    return !!token && !!userId;
  }

  private getLocalStorageItem(key: string): string | null {
    if (!this.isBrowser) {
      return null;
    }

    return window.localStorage.getItem(key);
  }

  private normalizeUserType(userType: string | null | undefined): string {
    const allowedTypes = ['candidate', 'company', 'admin', 'guest'];

    if (userType && allowedTypes.includes(userType)) {
      return userType;
    }

    return 'guest';
  }
}